import React, { useState } from 'react';
import { FileSpreadsheet, X, Check, CheckCircle, Edit2 } from 'lucide-react';
import { useAppData } from './context/DataContext';
import { Button } from 'antd';
import { Tooltip } from 'antd';
import { Input } from 'antd';
import Papa from 'papaparse';
import { CopyOutlined, FileExcelOutlined } from '@ant-design/icons';
import { ArrowLeftOutlined } from '@ant-design/icons';

const { TextArea } = Input;


const CSVColumnSelector = ({ headers, onColumnSelect, onCancel }) => {
    return (
        <div className="column-selector-container">
            <h3 className="column-selector-title">Select Email Column</h3>
            <p className="column-selector-description">
                Please select the column that contains email addresses:
            </p>
            <div className="column-selector-grid">
                {headers.map((header, index) => (
                    <button
                        key={index}
                        onClick={() => onColumnSelect(index)}
                        className="column-selector-button"
                    >
                        <span>{header || `Column ${index + 1}`}</span>
                        <span>→</span>
                    </button>
                ))}
            </div>
            <div className="action-buttons">
                <button onClick={onCancel} className="cancel-button">
                    Cancel
                </button>
            </div>
        </div>
    );
};

const CreateList = ({ initialName = '', initialEmails = [], onSave, onCancel }) => {
    const { users } = useAppData();

    const findExistingUser = (email) => {
        return users.find(user => user.email?.toLowerCase() === email.toLowerCase());
    };

    const [importMethod, setImportMethod] = useState('paste');
    const [importedContacts, setImportedContacts] = useState(
        initialEmails.map(email => {
            const existingUser = findExistingUser(email);
            return {
                email,
                username: existingUser?.username || '',
                phone: existingUser?.phone || '',
                verified: !!existingUser
            };
        })
    );

    const [errors, setErrors] = useState([]);
    const [pastedText, setPastedText] = useState('');
    const [uploadedFileName, setUploadedFileName] = useState('');
    const [editingIndex, setEditingIndex] = useState(null);
    const [editingEmail, setEditingEmail] = useState('');
    const [listName, setListName] = useState(initialName);
    const [csvData, setCSVData] = useState(null);
    const [showColumnSelector, setShowColumnSelector] = useState(false);

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const processContacts = (rawInput) => {
        let allEmails = [];
        let invalidEmails = [];

        if (Array.isArray(rawInput)) {
            allEmails = rawInput
                .filter(email => email)
                .map(email => email.toString().trim());
        } else {
            allEmails = rawInput
                .split(/[\s,;\n]+/)
                .map(email => email.trim())
                .filter(email => email);
        }

        // Separate valid and invalid emails
        const validEmails = [];
        allEmails.forEach(email => {
            if (validateEmail(email)) {
                validEmails.push(email);
            } else if (email) { // Only add non-empty invalid emails
                invalidEmails.push(email);
            }
        });

        const uniqueEmails = [...new Set(validEmails.map(email => email.toLowerCase()))];
        const duplicates = uniqueEmails.filter(email =>
            importedContacts.some(contact => contact.email.toLowerCase() === email)
        );

        const newContacts = uniqueEmails
            .filter(email => !duplicates.includes(email))
            .map(email => {
                const existingUser = findExistingUser(email);
                return {
                    email,
                    username: existingUser?.username || '',
                    phone: existingUser?.phone || '',
                    verified: !!existingUser
                };
            });

        setImportedContacts(prev => [...prev, ...newContacts]);

        // Collect all errors
        const allErrors = [];
        if (duplicates.length > 0) {
            allErrors.push(...duplicates.map(email => `Duplicate email: ${email}`));
        }
        if (invalidEmails.length > 0) {
            allErrors.push(...invalidEmails.map(email => `Invalid email format: ${email}`));
        }

        if (allErrors.length > 0) {
            setErrors(allErrors);
        }

        // Return info about processing results
        return {
            added: newContacts.length,
            invalid: invalidEmails.length,
            duplicates: duplicates.length
        };
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setUploadedFileName(file.name);
        setErrors([]);

        Papa.parse(file, {
            skipEmptyLines: true,
            header: true,
            complete: (results) => {
                setCSVData(results);
                setShowColumnSelector(true);
            },
            error: (error) => {
                setErrors([`Failed to parse CSV: ${error.message}`]);
                setUploadedFileName('');
            }
        });
    };

    const handleColumnSelect = (columnIndex) => {
        if (!csvData) return;

        // Extract emails from the selected column
        const emails = csvData.data.map(row => {
            const value = Object.values(row)[columnIndex];
            return value ? value.toString().trim() : '';
        });

        // Process the emails
        const processResults = processContacts(emails);
        if (processResults.invalid > 0 || processResults.duplicates > 0) {
            if (processResults.added === 0) {
                setUploadedFileName('');
            }
        }

        setShowColumnSelector(false);
        setCSVData(null);
    };

    const handlePastedText = () => {
        if (!pastedText.trim()) return;
        setErrors([]);
        const results = processContacts(pastedText);
        if (results.added > 0) {
            setPastedText('');
        }
    };

    const removeContact = (index) => {
        setImportedContacts(prev => prev.filter((_, i) => i !== index));
    };

    const startEditing = (index, email) => {
        setEditingIndex(index);
        setEditingEmail(email);
    };

    const handleEdit = (index) => {
        if (!validateEmail(editingEmail)) {
            setErrors(['Invalid email format']);
            return;
        }

        const existingUser = findExistingUser(editingEmail);
        setImportedContacts(prev => prev.map((contact, i) => {
            if (i === index) {
                return {
                    email: editingEmail,
                    username: existingUser?.username || '',
                    phone: existingUser?.phone || '',
                    verified: !!existingUser
                };
            }
            return contact;
        }));
        setEditingIndex(null);
        setEditingEmail('');
    };

    const handleSave = () => {
        if (!listName.trim()) {
            setErrors(['Please provide a list name']);
            return;
        }
        if (importedContacts.length === 0) {
            setErrors(['Please add at least one contact']);
            return;
        }
        const emailsArray = importedContacts.map(contact => contact.email);
        onSave(listName, emailsArray);
    };

    return (
        <>
            <div className="create-list-container">
            <Button
                onClick={onCancel}
                className="back-btn"
                icon={<ArrowLeftOutlined />}
            >
                Back
            </Button>
                <div className="import-container">

                <div className="list-name-container">
                    <label className="list-name-label">
                        List Name
                    </label>
                    <input
                        type="text"
                        value={listName}
                        onChange={(e) => setListName(e.target.value)}
                        className="list-name-input"
                        placeholder="Enter list name..."
                    />
                </div>

                <div className="import-methods">
                    <div
                        className={`import-method ${importMethod === 'paste' ? 'active' : ''}`}
                        onClick={() => setImportMethod('paste')}
                    >
                        <CopyOutlined className="import-method-icon" />
                        <div class="im-title-desc">
                            <div className="import-method-title">Paste List</div>
                            <div className="import-method-description">
                                Copy and paste your email list
                            </div>
                        </div>
                    </div>

                    <div
                        className={`import-method ${importMethod === 'file' ? 'active' : ''}`}
                        onClick={() => setImportMethod('file')}
                    >
                        <FileExcelOutlined className="import-method-icon" />
                        <div class="im-title-desc">
                            <div className="import-method-title">Upload CSV/Excel</div>
                            <div className="import-method-description">
                                Import from a spreadsheet
                            </div>
                        </div>
                    </div>
                </div>

                {importMethod === 'paste' && (
                    <div>
                        <TextArea
                            autoSize={{ minRows: 4 }}
                            className="paste-area"
                            placeholder="Paste your email list here...
Example formats:
john@email.com
jane@email.com, bob@email.com
amy@email.com; david@email.com
tim@email.com, sarah@email.com"
                            value={pastedText}
                            onChange={(e) => setPastedText(e.target.value)}
                        />
                        <Button
                            className="button"
                            onClick={handlePastedText}
                            disabled={!pastedText.trim()}
                        >
                            Import Contacts
                        </Button>
                    </div>
                )}

                {importMethod === 'file' && (
                    <div className="file-upload-container">
                        {showColumnSelector && csvData ? (
                            <CSVColumnSelector
                                headers={Object.keys(csvData.data[0] || {})}
                                onColumnSelect={handleColumnSelect}
                                onCancel={() => {
                                    setShowColumnSelector(false);
                                    setCSVData(null);
                                    setUploadedFileName('');
                                }}
                            />
                        ) : (
                            <>
                                <input
                                    type="file"
                                    accept=".csv,.xlsx,.xls"
                                    onChange={handleFileUpload}
                                    className="hidden"
                                    id="file-upload"
                                    style={{ display: "none" }}
                                />
                                <label
                                    htmlFor="file-upload"
                                    className="file-upload-area"
                                >
                                    <FileExcelOutlined className="file-upload-icon" />
                                    <div className="file-upload-text">
                                        Drop your CSV file here
                                    </div>
                                    <div className="file-upload-hint">
                                        or click to browse from your computer
                                    </div>
                                    {uploadedFileName && (
                                        <div className="file-name-container">
                                            <FileSpreadsheet size={16} className="text-blue-500" />
                                            <span className="file-name">{uploadedFileName}</span>
                                        </div>
                                    )}
                                </label>

                                <div className="format-helper">
                                    <div className="format-helper-title">File Format</div>
                                    <div className="format-helper-text">
                                        Your CSV file should contain a column with email addresses.
                                    </div>
                                    <div className="format-helper-text">
                                        Supported file types: .csv, .xlsx, .xls
                                    </div>
                                    <div className="format-example">
                                        Example: name@example.com
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {errors.length > 0 && (
                    <div className="error-alert">
                        <div className="error-title">Import Errors</div>
                        <ul className="error-list">
                            {errors.map((error, index) => (
                                <li key={index}>{error}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {importedContacts.length > 0 && (
                    <div className="contacts-container">
                        <div className="contacts-header">
                            Imported Contacts ({importedContacts.length})
                        </div>
                        <div className="contacts-list">
                            {importedContacts.map((contact, index) => (
                                <div key={index} className="contact-item">
                                    {contact.verified && (
                                        <Tooltip title={`${contact.username} is registered`}>
                                            <CheckCircle className="verified-icon" />
                                        </Tooltip>
                                    )}
                                    <div className="contact-details">
                                        {editingIndex === index ? (
                                            <div className="cl-email-approve">
                                                <Input

                                                    type="email"
                                                    value={editingEmail}
                                                    onChange={(e) => setEditingEmail(e.target.value)}
                                                    className="cl-email-input"
                                                />
                                                <Button
                                                    onClick={() => handleEdit(index)}
                                                    className="cl-approve-btn"
                                                >
                                                    <Check size={16} />
                                                </Button>
                                            </div>
                                        ) : (
                                            <div>
                                                <div className="contact-email">
                                                    {contact.email}
                                                </div>
                                                {contact.username && (
                                                    <div className="contact-info">
                                                        {contact.username}
                                                    </div>
                                                )}
                                                {contact.phone && (
                                                    <div className="contact-info">
                                                        {contact.phone}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="contact-actions">
                                        {editingIndex !== index && (
                                            <Button
                                                className="edit-button-1"
                                                onClick={() => startEditing(index, contact.email)}
                                            >
                                                <Edit2 size={16} />
                                            </Button>
                                        )}
                                        <Button
                                            className="remove-button"
                                            onClick={() => removeContact(index)}
                                        >
                                            <X size={16} />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="action-buttons">
                    <Button
                        onClick={onCancel}
                        className="cancel-button"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        className="save-button"
                        disabled={!listName.trim() || importedContacts.length === 0}
                    >
                        Save List
                    </Button>
                </div>
            </div>
            </div>
        </>
    );
};

export default CreateList;